import React, { useState, useEffect, useCallback } from 'react';
import { tasksAPI, Task, Comment, Attachment, User } from '../api/api';
import { useAuth } from '../context/AuthContext';
import UserSelector from './UserSelector';
import RichTextEditor from './RichTextEditor';
import './Modal.css';
import './TaskModal.css';

// Helper function to get API base URL
const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_URL;
  
  // Если задали явно, используем его
  if (envUrl) {
    return envUrl;
  }
  
  // Для Electron приложения
  if (typeof window !== 'undefined' && window.electronAPI !== undefined) {
    return 'http://task.clowoow.ru:5000/api';
  }
  
  // Для веб/вебвью по умолчанию используем боевой URL
  return 'https://prometey-task.clowoow.ru/api';
};

// Функция для проверки, содержит ли строка кириллицу или неправильную кодировку
const containsCyrillicOrBadEncoding = (str: string): boolean => {
  // Проверяем на кириллицу
  if (/[\u0400-\u04FF]/.test(str)) {
    return true;
  }
  // Проверяем на типичные признаки неправильной кодировки (mojibake)
  // Например: Ñ, Ð, Ð°, Ð¾ и т.д.
  if (/[ÑÐ][Ð°-Ñ]/.test(str) || /Ð[°-¿]/.test(str)) {
    return true;
  }
  return false;
};

// Функция для нормализации имени файла для отображения
const normalizeFileNameForDisplay = (fileName: string): string => {
  if (containsCyrillicOrBadEncoding(fileName)) {
    // Извлекаем расширение
    const lastDot = fileName.lastIndexOf('.');
    const ext = lastDot > 0 ? fileName.substring(lastDot) : '';
    return `file${ext}`;
  }
  return fileName;
};

// Функция для определения, можно ли просмотреть файл в браузере
const canViewInBrowser = (fileName: string): boolean => {
  const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  const viewableExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.txt', '.html', '.css', '.js', '.json', '.xml'];
  return viewableExtensions.includes(ext);
};

interface TaskModalProps {
  taskId: number;
  onClose: () => void;
  onTaskUpdated?: () => void;
}

const TaskModal: React.FC<TaskModalProps> = ({ taskId, onClose, onTaskUpdated }) => {
  const { isAdmin, isManager, user } = useAuth();
  const [task, setTask] = useState<Task | null>(null);
  const canEdit = isAdmin || isManager || (user && task?.created_by === user.id);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState<'open' | 'in_review' | 'completed'>('open');
  const [editAssignedTo, setEditAssignedTo] = useState<number | undefined>();
  const [editAssignedToIds, setEditAssignedToIds] = useState<number[]>([]);
  const [editReviewersIds, setEditReviewersIds] = useState<number[]>([]);
  const [editDeadline, setEditDeadline] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const loadTask = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tasksAPI.getById(taskId);
      setTask(data);
      setEditTitle(data.title);
      setEditDescription(data.description || '');
      setEditStatus(data.status);
      setEditAssignedTo(data.assigned_to);
      // Загружаем множественных исполнителей
      if (data.assignees && data.assignees.length > 0) {
        setEditAssignedToIds(data.assignees.map((a: any) => a.user_id));
      } else if (data.assigned_to_ids && data.assigned_to_ids.length > 0) {
        setEditAssignedToIds(data.assigned_to_ids);
      } else if (data.assigned_to) {
        setEditAssignedToIds([data.assigned_to]);
      } else {
        setEditAssignedToIds([]);
      }
      // Загружаем проверяющих (исключаем создателя задачи)
      let reviewers: number[] = [];
      if (data.reviewers && data.reviewers.length > 0) {
        reviewers = data.reviewers.map((r: any) => r.user_id);
      } else if (data.reviewers_ids && data.reviewers_ids.length > 0) {
        reviewers = data.reviewers_ids;
      }
      // Удаляем создателя из списка проверяющих, если он там есть
      if (data.created_by) {
        reviewers = reviewers.filter((id: number) => id !== data.created_by);
      }
      setEditReviewersIds(reviewers);
      // Преобразуем deadline в формат datetime-local (YYYY-MM-DDTHH:mm)
      if (data.deadline) {
        const deadlineDate = new Date(data.deadline);
        const year = deadlineDate.getFullYear();
        const month = String(deadlineDate.getMonth() + 1).padStart(2, '0');
        const day = String(deadlineDate.getDate()).padStart(2, '0');
        const hours = String(deadlineDate.getHours()).padStart(2, '0');
        const minutes = String(deadlineDate.getMinutes()).padStart(2, '0');
        setEditDeadline(`${year}-${month}-${day}T${hours}:${minutes}`);
      } else {
        setEditDeadline('');
      }
    } catch (error) {
      console.error('Ошибка при загрузке задачи:', error);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await tasksAPI.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Ошибка при загрузке пользователей:', error);
    }
  }, []);

  useEffect(() => {
    loadTask();
    loadUsers();
  }, [loadTask, loadUsers]);

  const handleAddComment = async () => {
    // Проверяем, есть ли текст (учитываем HTML теги)
    const textContent = commentText.replace(/<[^>]*>/g, '').trim();
    if (!textContent) return;

    try {
      await tasksAPI.addComment(taskId, commentText);
      setCommentText('');
      // Обновляем задачу, но НЕ закрываем модалку
      await loadTask();
      onTaskUpdated?.();
    } catch (error) {
      console.error('Ошибка при добавлении комментария:', error);
    }
  };

  const handleStartEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.content);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEditComment = async () => {
    if (!editingCommentId || !editingCommentText.trim()) return;

    try {
      await tasksAPI.updateComment(taskId, editingCommentId, editingCommentText);
      setEditingCommentId(null);
      setEditingCommentText('');
      loadTask();
      onTaskUpdated?.();
    } catch (error) {
      console.error('Ошибка при редактировании комментария:', error);
      alert('Не удалось отредактировать комментарий');
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Удалить комментарий?')) return;

    try {
      await tasksAPI.deleteComment(taskId, commentId);
      loadTask();
      onTaskUpdated?.();
    } catch (error) {
      console.error('Ошибка при удалении комментария:', error);
      alert('Не удалось удалить комментарий');
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    try {
      await tasksAPI.uploadAttachment(taskId, selectedFile);
      setSelectedFile(null);
      loadTask();
      onTaskUpdated?.();
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!window.confirm('Удалить вложение?')) return;

    try {
      await tasksAPI.deleteAttachment(taskId, attachmentId);
      loadTask();
      onTaskUpdated?.();
    } catch (error) {
      console.error('Ошибка при удалении вложения:', error);
    }
  };

  const handleViewAttachment = async (attachment: Attachment) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Требуется авторизация для просмотра файла');
        return;
      }

      const viewUrl = `${getApiBaseUrl()}/tasks/${taskId}/attachments/${attachment.id}/view`;
      const response = await fetch(viewUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при открытии файла');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Открываем файл в новом окне
      window.open(url, '_blank');
      // URL будет освобожден когда окно закроется или через некоторое время
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Ошибка при открытии файла:', error);
      alert('Не удалось открыть файл');
    }
  };

  const handleDownloadAttachment = async (e: React.MouseEvent<HTMLAnchorElement>, attachment: Attachment) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Требуется авторизация для скачивания файла');
        return;
      }

      const downloadUrl = `${getApiBaseUrl()}/tasks/${taskId}/attachments/${attachment.id}/download`;
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при скачивании файла');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Используем нормализованное имя для скачивания
      a.download = normalizeFileNameForDisplay(attachment.original_name);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      alert('Не удалось скачать файл');
    }
  };

  const handleSaveChanges = async () => {
    try {
      await tasksAPI.update(taskId, {
        title: editTitle,
        description: editDescription,
        status: editStatus,
        assigned_to: editAssignedToIds[0] || undefined, // для обратной совместимости
        assigned_to_ids: editAssignedToIds.length > 0 ? editAssignedToIds : undefined,
        reviewers_ids: editReviewersIds, // Всегда передаем массив, даже пустой, чтобы можно было удалить всех проверяющих
        deadline: editDeadline || null,
      });
      setEditing(false);
      loadTask();
      onTaskUpdated?.();
    } catch (error) {
      console.error('Ошибка при обновлении задачи:', error);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Загрузка...</div>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return '#3498db';
      case 'in_review':
        return '#9b59b6';
      case 'completed':
        return '#27ae60';
      default:
        return '#95a5a6';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content task-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Задача</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="task-modal-content">
          {!editing ? (
            <>
              <div className="task-details">
                <div className="task-title-section">
                  <h3>{task.title}</h3>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(task.status) }}
                  >
                    {task.status === 'open' ? 'Открыта' : task.status === 'in_review' ? 'На проверке' : 'Завершена'}
                  </span>
                </div>
                {task.description && <p className="task-description">{task.description}</p>}
                <div className="task-info">
                  <div>Создал: <strong>{task.created_by_username}</strong></div>
                  {task.assignees && task.assignees.length > 0 ? (
                    <div>
                      Исполнители: <strong>{task.assignees.map((a: any) => a.username).join(', ')}</strong>
                    </div>
                  ) : task.assigned_to_username && (
                    <div>Исполнитель: <strong>{task.assigned_to_username}</strong></div>
                  )}
                  {task.reviewers && task.reviewers.length > 0 && (
                    <div>
                      Проверяющие: <strong>{task.reviewers.map((r: any) => r.username).join(', ')}</strong>
                    </div>
                  )}
                  <div>Создана: {new Date(task.created_at).toLocaleString('ru-RU')}</div>
                  {task.deadline ? (
                    <div 
                      className="task-deadline-info"
                      style={{ 
                        color: new Date(task.deadline) < new Date() ? '#e74c3c' : '#2c3e50',
                        fontWeight: 'bold'
                      }}
                    >
                      Дедлайн: {new Date(task.deadline).toLocaleString('ru-RU')}
                      {new Date(task.deadline) < new Date() && ' (Просрочено)'}
                    </div>
                  ) : (
                    <div 
                      className="task-deadline-info"
                      style={{ 
                        color: '#2c3e50',
                        fontWeight: 'bold'
                      }}
                    >
                      Дедлайн: Задача со свободным сроком
                    </div>
                  )}
                </div>
                {canEdit && (
                  <button onClick={() => setEditing(true)} className="edit-btn">
                    Редактировать
                  </button>
                )}
              </div>

              <div className="task-sections">
                <div className="section">
                  <h4>Комментарии ({task.comments?.length || 0})</h4>
                  <div className="comments-list">
                    {task.comments?.map((comment: Comment) => (
                      <div key={comment.id} className="comment">
                        <div className="comment-header">
                          <strong>{comment.username}</strong>
                          <div className="comment-header-right">
                            <span className="comment-date">
                              {new Date(comment.created_at).toLocaleString('ru-RU')}
                            </span>
                            {user && comment.user_id === user.id && (
                              <div className="comment-actions">
                                {editingCommentId === comment.id ? (
                                  <>
                                    <button
                                      onClick={handleSaveEditComment}
                                      className="comment-action-btn save-btn"
                                      title="Сохранить"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={handleCancelEditComment}
                                      className="comment-action-btn cancel-btn"
                                      title="Отмена"
                                    >
                                      ✕
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleStartEditComment(comment)}
                                      className="comment-action-btn edit-btn"
                                      title="Редактировать"
                                    >
                                      ✎
                                    </button>
                                    <button
                                      onClick={() => handleDeleteComment(comment.id)}
                                      className="comment-action-btn delete-btn"
                                      title="Удалить"
                                    >
                                      🗑
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {editingCommentId === comment.id ? (
                          <RichTextEditor
                            value={editingCommentText}
                            onChange={setEditingCommentText}
                            rows={3}
                          />
                        ) : (
                          <div
                            className="comment-content"
                            dangerouslySetInnerHTML={{ __html: comment.content }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="add-comment">
                    <RichTextEditor
                      value={commentText}
                      onChange={setCommentText}
                      placeholder="Добавить комментарий..."
                      rows={3}
                    />
                    <button onClick={handleAddComment} className="add-btn">
                      Добавить
                    </button>
                  </div>
                </div>

                <div className="section">
                  <h4>Вложения ({task.attachments?.length || 0})</h4>
                  <div className="attachments-list">
                    {task.attachments?.map((attachment: Attachment) => {
                      const displayName = normalizeFileNameForDisplay(attachment.original_name);
                      const canView = canViewInBrowser(attachment.original_name);
                      return (
                        <div key={attachment.id} className="attachment">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (canView) {
                                handleViewAttachment(attachment);
                              } else {
                                handleDownloadAttachment(e, attachment);
                              }
                            }}
                            className="attachment-link"
                            title={canView ? "Просмотр" : "Скачать"}
                          >
                            <span>📎</span>
                            <span className="attachment-link-text">{displayName}</span>
                          </a>
                          <div className="attachment-actions">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownloadAttachment(e as any, attachment);
                              }}
                              className="download-btn"
                              title="Скачать"
                            >
                              ⬇️ Скачать
                            </button>
                            <button
                              onClick={() => handleDeleteAttachment(attachment.id)}
                              className="delete-btn"
                              title="Удалить"
                            >
                              Удалить
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="add-attachment">
                    <input
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    />
                    {selectedFile && (
                      <button onClick={handleFileUpload} className="add-btn">
                        Загрузить
                      </button>
                    )}
                  </div>
                </div>

              </div>
            </>
          ) : (
            <div className="edit-form">
              <div className="form-group">
                <label>Название</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Статус</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as any)}
                >
                  <option value="open">Открыта</option>
                  <option value="in_review">На проверке</option>
                  <option value="completed">Завершена</option>
                </select>
              </div>
              <UserSelector
                label="Исполнители"
                users={users}
                selectedIds={editAssignedToIds}
                onChange={(ids) => {
                  setEditAssignedToIds(ids);
                  setEditAssignedTo(ids[0]); // для обратной совместимости
                }}
                placeholder="Выберите исполнителей"
              />
              <UserSelector
                label="Проверяющие"
                users={users}
                selectedIds={editReviewersIds}
                onChange={setEditReviewersIds}
                placeholder="Выберите проверяющих"
                hint="Если не выбраны, проверять может только создатель задачи"
                excludeUserIds={task?.created_by ? [task.created_by] : []}
              />
              <div className="form-group">
                <label>Дедлайн</label>
                <input
                  type="datetime-local"
                  value={editDeadline}
                  onChange={(e) => setEditDeadline(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button onClick={handleSaveChanges} className="submit-btn">
                  Сохранить
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskModal;

